import { useState, type ReactNode } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowRight,
  BookOpen,
  ChevronRight,
  CreditCard,
  Images,
  LifeBuoy,
  Mail,
  MessageCircle,
  Printer,
  Receipt,
  Rocket,
  Search,
  ShieldCheck,
  ShoppingBag,
  Download,
  Tag,
  ThumbsDown,
  ThumbsUp,
  UploadCloud,
  Users,
  X,
  type LucideIcon,
} from "lucide-react";
import AppHeader from "@/components/AppHeader";
import Footer from "@/components/Footer";
import NotFound from "@/pages/NotFound";
import { GuideBlocks, guideLink } from "@/components/guides/GuideBody";
import { renderInline } from "@/lib/miniMarkdown";
import { useHostname } from "@/lib/ssr";
import { isHelpEnabled } from "@/lib/helpFlag";
import {
  ALL_HELP,
  helpContentVersion,
  HELP_LANGS,
  HELP_PATH,
  HELP_CATEGORIES,
  categoryArticles,
  getCategory,
  getHelpArticle,
  getSection,
  helpArticlePath,
  helpCategoryPath,
  helpLangs,
  helpSectionPath,
  hubSections,
  isSeparateSection,
  label,
  listHelp,
  relatedArticles,
  sectionCategories,
  type HelpArticle,
  type HelpCategory,
  type HelpSectionId,
} from "@/content/help";
import { helpImagesFor } from "@/content/help/images";
import { buildHelpIndex, queryTerms, searchHelp } from "@/content/help/search";
import { Highlight, HelpSearchBox, helpSearchUrl, readHelpFilters } from "./HelpSearch";
import type { SeoLang } from "@/lib/seo/site";
import HelpAppPromo from "./HelpAppPromo";
import { formatHelpDate, useHelpContent, useHelpLang, useHelpTo } from "./helpLinks";

// The help centre: /help (search + the hub's sections), /help/<section>, /help/<section>/<category> and
// /help/articles/<slug>. The Photohawk app's section (/help/app) is a separate one (src/content/help/sections.ts): not
// on the hub, searched only from its own pages, its own contact block, breadcrumbs that start at the section. Server-rendered from the help store (src/content/help, ./remote.ts); the <head> (title, canonical, hreflang, Open Graph,
// TechArticle / BreadcrumbList / FAQPage JSON-LD) comes from RouteMeta via src/lib/seo/help.ts. Where the help centre
// is off (prod until the owner approves it: src/lib/helpFlag.ts) every page is a real 404.

const ICONS: Record<string, LucideIcon> = {
  rocket: Rocket,
  upload: UploadCloud,
  images: Images,
  users: Users,
  card: CreditCard,
  search: Search,
  bag: ShoppingBag,
  receipt: Receipt,
  shield: ShieldCheck,
  tag: Tag,
  printer: Printer,
  download: Download,
  message: MessageCircle,
  lifebuoy: LifeBuoy,
};
const iconFor = (c: HelpCategory) => ICONS[c.icon] ?? BookOpen;

const HelpOnly = ({ children }: { children: ReactNode }) => (isHelpEnabled(useHostname()) ? <>{children}</> : <NotFound />);

let INDEX: ReturnType<typeof buildHelpIndex> | null = null;
let INDEX_VERSION = -1;
/** The search index over every loaded article; rebuilt when a language loads. */
const helpIndex = () => {
  if (!INDEX || INDEX_VERSION !== helpContentVersion()) {
    INDEX = buildHelpIndex(ALL_HELP);
    INDEX_VERSION = helpContentVersion();
  }
  return INDEX;
};

const Shell = ({ children, band }: { children: ReactNode; band: ReactNode }) => (
  <div className="min-h-screen bg-background">
    <AppHeader variant="marketing" />
    <main>
      {/* No overflow-hidden here: the search suggestions hang below the band. The glow is clipped by its own layer. */}
      <section className="relative bg-hawk-navy pb-14 pt-28 text-white md:pb-16 md:pt-32">
        <div className="absolute inset-0 overflow-hidden" aria-hidden="true">
          <div className="absolute inset-0 bg-radial-orange opacity-20" />
        </div>
        <div className="container relative z-10 mx-auto max-w-5xl px-4">{band}</div>
      </section>
      {children}
    </main>
    <Footer />
  </div>
);

const Crumbs = ({ items }: { items: { to?: string; label: string }[] }) => (
  <nav aria-label="Breadcrumb" className="mb-5" data-testid="help-breadcrumbs">
    <ol className="m-0 flex list-none flex-wrap items-center gap-1 p-0 text-sm text-white/70">
      {items.map((it, i) => (
        <li key={i} className="inline-flex items-center gap-1">
          {i > 0 && <ChevronRight size={14} aria-hidden="true" />}
          {it.to ? (
            <Link to={it.to} className="hover:text-white">
              {it.label}
            </Link>
          ) : (
            <span aria-current="page" className="text-white">
              {it.label}
            </span>
          )}
        </li>
      ))}
    </ol>
  </nav>
);

/** The breadcrumbs' first items: the hub, then the section, except that a separate section starts at itself. */
const sectionCrumbs = (to: (p: string) => string, t: (k: string) => string, section: HelpSectionId, linkSection: boolean) => [
  ...(isSeparateSection(section) ? [] : [{ to: to(HELP_PATH), label: t("help.breadcrumbHome") }]),
  { to: linkSection ? to(helpSectionPath(section)) : undefined, label: t(`help.sections.${section}.title`) },
];

/** The address support reads, and the subject the Photohawk app's help suggests (so app messages can be told from
 *  Studio's; the app's own "Email us" uses the same prefix: photohawk-personal LegalLinks.supportEmail). */
export const SUPPORT_EMAIL = "support@photohawk.com";
export const APP_SUPPORT_SUBJECT = "Photohawk app: ";
const mailto = (subject?: string) => `mailto:${SUPPORT_EMAIL}${subject ? `?subject=${encodeURIComponent(subject)}` : ""}`;

const ArticleRow = ({ a }: { a: HelpArticle }) => {
  const to = useHelpTo();
  return (
    <li className="border-b border-border last:border-0">
      <Link to={to(helpArticlePath(a.slug))} className="group flex items-start gap-3 py-3">
        <span className="min-w-0 flex-1">
          <span className="block font-semibold group-hover:text-hawk-orange">{a.title}</span>
          <span className="block text-sm text-muted-foreground">{a.description}</span>
        </span>
        <ArrowRight size={16} className="mt-1 flex-none text-hawk-orange transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
      </Link>
    </li>
  );
};

const CategoryCard = ({ c, lang, max = 3 }: { c: HelpCategory; lang: SeoLang; max?: number }) => {
  const { t } = useTranslation();
  const to = useHelpTo();
  const articles = categoryArticles(c.id, lang);
  const Icon = iconFor(c);
  return (
    <div className="flex h-full flex-col rounded-2xl border border-border bg-card p-5 shadow-sm" data-testid={`help-category-${c.id}`}>
      <Link to={to(helpCategoryPath(c.section, c.id))} className="group mb-2 flex items-center gap-3">
        <span aria-hidden="true" className="flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-hawk-orange/10 text-[hsl(18_100%_38%)]">
          <Icon size={20} />
        </span>
        <span className="min-w-0">
          <span className="block font-display text-lg font-bold leading-snug group-hover:text-hawk-orange">{label(c.title, lang)}</span>
          <span className="block text-xs text-muted-foreground">{t("help.articles", { count: articles.length })}</span>
        </span>
      </Link>
      <p className="mb-2 mt-0 text-sm text-muted-foreground">{label(c.description, lang)}</p>
      <ul className="m-0 flex-1 list-none p-0 text-sm">
        {articles.slice(0, max).map((a) => (
          <li key={a.slug} className="py-1">
            <Link to={to(helpArticlePath(a.slug))} className="text-foreground/85 underline-offset-2 hover:text-hawk-orange hover:underline">
              {a.title}
            </Link>
          </li>
        ))}
      </ul>
      {articles.length > max && (
        <Link to={to(helpCategoryPath(c.section, c.id))} className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-[hsl(18_100%_38%)] hover:underline">
          {t("help.browse")} <ArrowRight size={14} aria-hidden="true" />
        </Link>
      )}
    </div>
  );
};

const SectionBlock = ({ id, lang, alt }: { id: HelpSectionId; lang: SeoLang; alt?: boolean }) => {
  const { t } = useTranslation();
  const to = useHelpTo();
  const cats = sectionCategories(id).filter((c) => categoryArticles(c.id).length);
  if (!cats.length) return null;
  return (
    <section id={id} aria-labelledby={`${id}-h`} className={`scroll-mt-24 py-12 md:py-14 ${alt ? "bg-secondary/30" : ""}`} data-testid={`help-section-${id}`}>
      <div className="container mx-auto max-w-5xl px-4">
        <h2 id={`${id}-h`} className="mb-1 font-display text-3xl font-bold">
          <Link to={to(helpSectionPath(id))} className="hover:text-hawk-orange">
            {t(`help.sections.${id}.title`)}
          </Link>
        </h2>
        <p className="mb-8 text-muted-foreground">{t(`help.sections.${id}.blurb`)}</p>
        <ul className="m-0 grid list-none gap-5 p-0 md:grid-cols-2">
          {cats.map((c) => (
            <li key={c.id}>
              <CategoryCard c={c} lang={lang} />
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};

const Contact = ({ section }: { section?: HelpSectionId }) => {
  const { t } = useTranslation();
  // The Photohawk app's help: email only, with the app's subject prefix (the chat is Studio's Intercom Messenger).
  const app = section === "app";
  return (
    <section aria-labelledby="help-contact-h" className="border-t border-border py-12" data-testid="help-contact">
      <div className="container mx-auto max-w-5xl px-4">
        <div className="flex flex-col gap-5 rounded-2xl border border-border bg-card p-6 md:flex-row md:items-center">
          <span aria-hidden="true" className="flex h-12 w-12 flex-none items-center justify-center rounded-full bg-gradient-to-br from-hawk-orange to-hawk-gold text-white">
            <LifeBuoy size={24} />
          </span>
          <div className="min-w-0 flex-1">
            <h2 id="help-contact-h" className="m-0 font-display text-xl font-bold">
              {t("help.contact.title")}
            </h2>
            <p className="mb-0 mt-1 text-sm text-muted-foreground">{t(app ? "help.contact.appBody" : "help.contact.body")}</p>
            {section === "photos" && <p className="mb-0 mt-2 text-sm text-muted-foreground">{t("help.contact.buyerNote")}</p>}
          </div>
          <div className="flex flex-wrap gap-2">
            <a
              href={mailto(app ? APP_SUPPORT_SUBJECT : undefined)}
              className={`inline-flex min-h-11 items-center gap-2 rounded-lg px-4 text-sm font-semibold ${app ? "bg-hawk-navy text-white hover:opacity-90" : "border border-border hover:bg-secondary"}`}
              data-testid="help-contact-email"
            >
              <Mail size={16} aria-hidden="true" />
              {t("help.contact.email")}
            </a>
            {/* Intercom Messenger (src/components/Intercom.tsx) opens on any .launch-intercom element. */}
            {!app && (
              <button type="button" className="launch-intercom inline-flex min-h-11 items-center gap-2 rounded-lg bg-hawk-navy px-4 text-sm font-semibold text-white hover:opacity-90">
                <MessageCircle size={16} aria-hidden="true" />
                {t("help.contact.chat")}
              </button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

/** The articles to suggest when a search finds nothing: the first article of each category, up to six (a separate
 *  section's own when the search is in it; the hub's otherwise). */
const topArticles = (lang: SeoLang, section?: string): HelpArticle[] =>
  HELP_CATEGORIES.filter((c) => (isSeparateSection(section) ? c.section === section : !isSeparateSection(c.section)))
    .flatMap((c) => categoryArticles(c.id, lang).slice(0, 1))
    .slice(0, 6);

/** Search results (and filtered lists), with the matched words highlighted; a helpful empty state. */
const Results = ({ q, lang, hits, section, category }: { q: string; lang: SeoLang; hits: { slug: string; title: string; description: string; section: string; category: string }[]; section?: string; category?: string }) => {
  const { t } = useTranslation();
  const { search } = useLocation();
  const to = useHelpTo();
  const terms = queryTerms(q);
  const cat = category ? HELP_CATEGORIES.find((c) => c.id === category) : undefined;
  const scope = cat ? label(cat.title, lang) : section ? t(`help.sections.${section}.title`) : "";
  const heading = q
    ? hits.length
      ? t("help.search.results", { count: hits.length, q })
      : t("help.search.noResults", { q })
    : t("help.search.filtered", { count: hits.length, name: scope });
  return (
    <section aria-labelledby="help-results-h" className="py-10" data-testid="help-results">
      <div className="container mx-auto max-w-3xl px-4">
        <h2 id="help-results-h" className="mb-1 font-display text-2xl font-bold">
          {heading}
        </h2>
        {q && scope && <p className="mb-4 mt-0 text-sm text-muted-foreground">{t("help.search.inSection", { name: scope })}</p>}
        {hits.length > 0 ? (
          <ul className="m-0 mt-4 list-none p-0">
            {hits.map((h) => (
              <li key={h.slug} className="border-b border-border last:border-0">
                <Link to={to(helpArticlePath(h.slug))} className="group block py-3">
                  <span className="block font-semibold group-hover:text-hawk-orange">
                    <Highlight text={h.title} terms={terms} />
                  </span>
                  <span className="block text-sm text-muted-foreground">
                    <Highlight text={h.description} terms={terms} />
                  </span>
                  <span className="mt-1 block text-xs text-muted-foreground">{t(`help.sections.${h.section}.title`)}</span>
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <div className="mt-4 space-y-4" data-testid="help-no-results">
            <p className="m-0 text-muted-foreground">{t("help.search.noResultsHint")}</p>
            <div className="flex flex-wrap gap-3 text-sm font-semibold">
              {(section || category) && (
                <Link to={helpSearchUrl(search, { q, section: undefined, category: undefined })} className="text-[hsl(18_100%_38%)] hover:underline">
                  {t("help.search.clearFilters")}
                </Link>
              )}
              <a href="#help-contact-h" className="text-[hsl(18_100%_38%)] hover:underline">
                {t("help.search.contactLink")}
              </a>
            </div>
            <h3 className="mb-0 mt-6 font-display text-lg font-bold">{t("help.search.topArticles")}</h3>
            <ul className="m-0 list-none p-0">
              {topArticles(lang, section).map((a) => (
                <ArticleRow key={a.slug} a={a} />
              ))}
            </ul>
          </div>
        )}
        <Link to={to(HELP_PATH)} className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-[hsl(18_100%_38%)] hover:underline">
          <X size={14} aria-hidden="true" /> {t("help.search.clear")}
        </Link>
      </div>
    </section>
  );
};

/** /help (and /help?q=…, with ?section= / ?category= filters). */
const HelpHome = () => {
  const { t } = useTranslation();
  const { search } = useLocation();
  const lang = useHelpLang(HELP_LANGS);
  useHelpContent(lang);
  const q = new URLSearchParams(search).get("q")?.trim() ?? "";
  const filters = readHelpFilters(search);
  const filtered = !!(filters.section || filters.category);
  // A query: the matches (title matches first), within the filters. Filters alone: every article they cover.
  const hits = q
    ? searchHelp(helpIndex(), q, lang, { limit: 50, section: filters.section, category: filters.category })
    : filtered
      ? listHelp(lang)
          .filter((a) => (!filters.section || a.section === filters.section) && (!filters.category || a.category === filters.category))
          .map((a) => ({ slug: a.slug, title: a.title, description: a.description, section: a.section, category: a.category, score: 0 }))
      : [];
  return (
    <Shell
      band={
        <>
          <span className="mb-5 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-1.5 text-sm font-medium">
            <LifeBuoy size={16} className="text-hawk-orange" aria-hidden="true" />
            {t("help.hero.eyebrow")}
          </span>
          <h1 className="mb-3 font-display text-4xl font-bold md:text-5xl">{t("help.hero.title")}</h1>
          <p className="mb-7 max-w-2xl text-lg text-white/80">{t("help.hero.subtitle")}</p>
          <HelpSearchBox key={q} index={helpIndex} lang={lang} initial={q} onHub />
          {!q && !filtered && (
            <nav aria-label={t("help.breadcrumbHome")} className="mt-7 flex flex-wrap gap-2">
              {hubSections().map((s) => (
                <a key={s.id} href={`#${s.id}`} className="inline-flex min-h-11 items-center rounded-full border border-white/25 px-4 text-sm font-semibold hover:bg-white/10">
                  {t(`help.sections.${s.id}.title`)}
                </a>
              ))}
            </nav>
          )}
        </>
      }
    >
      {q || filtered ? (
        <Results q={q} lang={lang} hits={hits} section={filters.section} category={filters.category} />
      ) : (
        hubSections().map((s, i) => <SectionBlock key={s.id} id={s.id} lang={lang} alt={i % 2 === 1} />)
      )}
      <Contact section={filters.section} />
    </Shell>
  );
};

/** /help/:section and /help/:section/:category. */
const HelpListing = () => {
  const { t } = useTranslation();
  const { section = "", category } = useParams();
  const lang = useHelpLang(HELP_LANGS);
  useHelpContent(lang);
  const to = useHelpTo();
  const s = getSection(section);
  const cat = category ? getCategory(section, category) : undefined;
  if (!s || (category && (!cat || !categoryArticles(cat.id).length))) return <NotFound />;
  const sectionTitle = t(`help.sections.${s.id}.title`);
  return (
    <Shell
      band={
        <>
          <Crumbs items={[...sectionCrumbs(to, t, s.id, !!cat), ...(cat ? [{ label: label(cat.title, lang) }] : [])]} />
          <h1 className="mb-3 font-display text-4xl font-bold md:text-5xl">{cat ? label(cat.title, lang) : sectionTitle}</h1>
          <p className="mb-7 max-w-2xl text-lg text-white/80">{cat ? label(cat.description, lang) : t(`help.sections.${s.id}.blurb`)}</p>
          <HelpSearchBox index={helpIndex} lang={lang} scope={isSeparateSection(s.id) ? s.id : undefined} />
        </>
      }
    >
      <section className="py-10 md:py-12">
        <div className="container mx-auto max-w-5xl px-4">
          {cat ? (
            <ul className="m-0 max-w-3xl list-none p-0" data-testid="help-category-articles">
              {categoryArticles(cat.id, lang).map((a) => (
                <ArticleRow key={a.slug} a={a} />
              ))}
            </ul>
          ) : (
            <ul className="m-0 grid list-none gap-5 p-0 md:grid-cols-2">
              {sectionCategories(s.id)
                .filter((c) => categoryArticles(c.id).length)
                .map((c) => (
                  <li key={c.id}>
                    <CategoryCard c={c} lang={lang} max={8} />
                  </li>
                ))}
            </ul>
          )}
        </div>
      </section>
      <Contact section={s.id} />
    </Shell>
  );
};

const HELPFUL_KEY = "ph-help-feedback";

/**
 * "Was this article helpful?": anonymous. The vote stays in this browser (so the buttons remember it) and is pushed
 * to window.dataLayer as { event: "help_feedback", article, lang, helpful } when an analytics tag is present; nothing
 * about the visitor is collected. "No" offers email and chat.
 */
const Helpful = ({ a }: { a: HelpArticle }) => {
  const { t } = useTranslation();
  const app = a.section === "app";
  const [vote, setVote] = useState<"yes" | "no" | null>(null);
  const cast = (v: "yes" | "no") => {
    setVote(v);
    try {
      const all = JSON.parse(localStorage.getItem(HELPFUL_KEY) || "{}") as Record<string, string>;
      all[a.slug] = v;
      localStorage.setItem(HELPFUL_KEY, JSON.stringify(all));
    } catch {
      /* storage blocked: the vote still shows for this visit */
    }
    const w = window as unknown as { dataLayer?: unknown[] };
    if (Array.isArray(w.dataLayer)) w.dataLayer.push({ event: "help_feedback", article: a.slug, lang: a.lang, helpful: v === "yes" });
  };
  return (
    <section aria-label={t("help.helpful.question")} className="mt-10 max-w-[68ch] rounded-2xl border border-border bg-card p-5" data-testid="help-helpful">
      {vote === null ? (
        <div className="flex flex-wrap items-center gap-3">
          <p className="m-0 font-semibold">{t("help.helpful.question")}</p>
          <div className="flex gap-2">
            <button type="button" onClick={() => cast("yes")} className="inline-flex min-h-11 items-center gap-1.5 rounded-lg border border-border px-4 text-sm font-semibold hover:bg-secondary">
              <ThumbsUp size={16} aria-hidden="true" /> {t("help.helpful.yes")}
            </button>
            <button type="button" onClick={() => cast("no")} className="inline-flex min-h-11 items-center gap-1.5 rounded-lg border border-border px-4 text-sm font-semibold hover:bg-secondary">
              <ThumbsDown size={16} aria-hidden="true" /> {t("help.helpful.no")}
            </button>
          </div>
        </div>
      ) : vote === "yes" ? (
        <p role="status" className="m-0 font-semibold">
          {t("help.helpful.thanks")}
        </p>
      ) : (
        <div role="status">
          <p className="mb-3 mt-0 font-semibold">{t("help.helpful.sorry")}</p>
          <div className="flex flex-wrap gap-2">
            <a
              href={mailto(`${app ? APP_SUPPORT_SUBJECT : ""}Help article: ${a.title}`)}
              className="inline-flex min-h-11 items-center gap-2 rounded-lg border border-border px-4 text-sm font-semibold hover:bg-secondary"
            >
              <Mail size={16} aria-hidden="true" /> {t("help.contact.email")}
            </a>
            {!app && (
              <button type="button" className="launch-intercom inline-flex min-h-11 items-center gap-2 rounded-lg bg-hawk-navy px-4 text-sm font-semibold text-white hover:opacity-90">
                <MessageCircle size={16} aria-hidden="true" /> {t("help.contact.chat")}
              </button>
            )}
          </div>
        </div>
      )}
    </section>
  );
};

/** How many related articles the desktop sidebar lists (the section under the article lists them all). */
const SIDEBAR_RELATED = 5;

/** /help/articles/:slug */
const HelpArticlePage = () => {
  const { t } = useTranslation();
  const { slug = "" } = useParams();
  const langs = helpLangs(slug);
  const lang = useHelpLang(langs);
  const ready = useHelpContent(lang);
  const { search } = useLocation();
  const to = useHelpTo();
  const a = getHelpArticle(slug, lang);
  if (!a) return <NotFound />;
  const cat = getCategory(a.section, a.category)!;
  const related = relatedArticles(a);
  const asked = new URLSearchParams(search).get("lang");
  // (While a language is still loading in the browser the page shows English; that isn't a fallback.)
  const fellBack = ready && !!asked && asked !== "en" && a.lang === "en";
  return (
    <Shell
      band={
        <>
          <Crumbs items={[...sectionCrumbs(to, t, a.section, true), { to: to(helpCategoryPath(a.section, a.category)), label: label(cat.title, a.lang) }]} />
          <h1 className="mb-4 max-w-4xl font-display text-[clamp(2rem,4.5vw,3rem)] font-bold leading-[1.1] tracking-tight">{a.title}</h1>
          <p className="mb-5 max-w-3xl text-lg text-white/80">{a.description}</p>
          <p className="m-0 text-sm text-white/70">{t("help.article.updated", { date: formatHelpDate(a.updated, a.lang) })}</p>
        </>
      }
    >
      <article data-testid="help-article" lang={a.lang} className="container mx-auto max-w-5xl px-4 py-10 md:py-12">
        <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_15rem] lg:gap-12">
          <div className="min-w-0">
            {fellBack && (
              <p role="note" className="mb-6 max-w-[68ch] rounded-lg border border-border bg-secondary/40 px-4 py-2 text-sm">
                {t("help.article.notTranslated")}
              </p>
            )}
            <div className="guide-prose" data-testid="help-body">
              <GuideBlocks blocks={a.blocks} images={helpImagesFor(a.blocks)} />
              {a.faq.length > 0 && (
                <section aria-labelledby="faq" data-testid="help-faq">
                  <h2 id="faq">{t("help.article.faq")}</h2>
                  {a.faq.map((f, i) => (
                    <div key={i} className="guide-faq">
                      <h3>{f.q}</h3>
                      <p>{renderInline(f.a, guideLink, `faq${i}`)}</p>
                    </div>
                  ))}
                </section>
              )}
            </div>
            {a.appPromo === "auto" && <HelpAppPromo />}
            <Helpful a={a} />
          </div>
          {(a.headings.length > 2 || related.length > 0) && (
            <aside className="hidden lg:block" data-testid="help-sidebar">
              <div className="sticky top-28 space-y-8">
                {a.headings.length > 2 && (
                  <nav aria-labelledby="help-toc-h">
                    <p id="help-toc-h" className="mb-3 text-xs font-bold uppercase tracking-[0.12em] text-muted-foreground">
                      {t("help.article.onThisPage")}
                    </p>
                    <ol className="m-0 list-none space-y-1 border-l border-border p-0">
                      {a.headings.map((h) => (
                        <li key={h.id}>
                          <a href={`#${h.id}`} className="-ml-px block border-l-2 border-transparent py-1 pl-3 text-sm text-foreground/75 hover:border-hawk-orange hover:text-hawk-orange">
                            {h.text}
                          </a>
                        </li>
                      ))}
                    </ol>
                  </nav>
                )}
                {related.length > 0 && (
                  // The first few related articles beside the text on wide screens; the full list stays at the bottom.
                  <nav aria-labelledby="help-related-side-h" data-testid="help-related-side">
                    <p id="help-related-side-h" className="mb-3 text-xs font-bold uppercase tracking-[0.12em] text-muted-foreground">
                      {t("help.article.related")}
                    </p>
                    <ul className="m-0 list-none space-y-1 border-l border-border p-0">
                      {related.slice(0, SIDEBAR_RELATED).map((r) => (
                        <li key={r.slug}>
                          <Link
                            to={to(helpArticlePath(r.slug))}
                            className="-ml-px block border-l-2 border-transparent py-1 pl-3 text-sm leading-snug text-foreground/75 hover:border-hawk-orange hover:text-hawk-orange"
                          >
                            {r.title}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </nav>
                )}
              </div>
            </aside>
          )}
        </div>
      </article>
      {related.length > 0 && (
        <section aria-labelledby="help-related-h" className="border-t border-border bg-secondary/30 py-12" data-testid="help-related">
          <div className="container mx-auto max-w-5xl px-4">
            <h2 id="help-related-h" className="mb-4 font-display text-2xl font-bold">
              {t("help.article.related")}
            </h2>
            <ul className="m-0 grid list-none gap-x-8 p-0 md:grid-cols-2">
              {related.map((r) => (
                <ArticleRow key={r.slug} a={r} />
              ))}
            </ul>
          </div>
        </section>
      )}
      <Contact section={a.section} />
    </Shell>
  );
};

export const HelpIndexRoute = () => (
  <HelpOnly>
    <HelpHome />
  </HelpOnly>
);
export const HelpListingRoute = () => (
  <HelpOnly>
    <HelpListing />
  </HelpOnly>
);
export const HelpArticleRoute = () => (
  <HelpOnly>
    <HelpArticlePage />
  </HelpOnly>
);
